import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, ShoppingBag, Lock } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { scrollToId } from "@/hooks/useLenis";

export const CartDrawer = () => {
  const { items, open, setOpen, inc, dec, remove, clear, subtotal, count } = useCart();
  const { t, lang, format, formatRaw } = useLang();
  const sub = subtotal(lang);
  const vat = sub * 0.2;

  const checkout = () => {
    toast.success(t.cart.checkoutToast);
    clear();
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            data-testid="cart-overlay"
          />
          <motion.aside
            className="fixed top-0 right-0 bottom-0 z-[70] w-full max-w-md glass border-l border-[#00f2fe]/20 flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 34 }}
            data-testid="cart-drawer"
            aria-label="Cart"
          >
            <div className="flex items-center justify-between px-6 h-20 border-b border-white/[0.06]">
              <div>
                <h2 className="font-display font-bold text-xl text-[#f4f2ec]" data-testid="cart-title">{t.cart.title}</h2>
                <span className="font-mono text-[0.62rem] tracking-[0.2em] uppercase text-[#666]" data-testid="cart-count-label">
                  {count} {t.cart.items}
                </span>
              </div>
              <button className="qty-btn !w-10 !h-10 !rounded-full" onClick={() => setOpen(false)} data-testid="cart-close-button" aria-label="Close">
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center gap-4" data-testid="cart-empty">
                  <div className="icon-tile !w-16 !h-16 !rounded-2xl">
                    <ShoppingBag size={24} strokeWidth={1.4} />
                  </div>
                  <p className="font-display font-bold text-lg text-[#f4f2ec]">{t.cart.empty}</p>
                  <p className="text-sm text-[#777] max-w-xs">{t.cart.emptyDesc}</p>
                  <button className="btn-ghost mt-2" onClick={() => { setOpen(false); scrollToId("catalog"); }} data-testid="cart-browse-button">
                    {t.cart.browse}
                  </button>
                </div>
              ) : (
                items.map((i) => (
                  <motion.div
                    layout
                    key={i.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="flex gap-4 p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]"
                    data-testid={`cart-item-${i.id}`}
                  >
                    <img src={i.image} alt="" className="w-20 h-20 rounded-lg object-cover saturate-[0.7]" />
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between gap-3">
                        <h4 className="font-display font-semibold text-sm text-[#f4f2ec] truncate">{i.name[lang]}</h4>
                        <button className="text-[#666] hover:text-red-400 transition-colors" onClick={() => remove(i.id)} data-testid={`cart-remove-${i.id}`} aria-label="Remove">
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div className="font-mono text-[0.6rem] tracking-[0.18em] uppercase text-[#666] mt-0.5">{i.tag[lang]}</div>
                      <div className="mt-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <button className="qty-btn" onClick={() => dec(i.id)} data-testid={`cart-dec-${i.id}`} aria-label="Decrease"><Minus size={12} /></button>
                          <span className="font-mono text-sm w-6 text-center text-[#f4f2ec]" data-testid={`cart-qty-${i.id}`}>{i.qty}</span>
                          <button className="qty-btn" onClick={() => inc(i.id)} data-testid={`cart-inc-${i.id}`} aria-label="Increase"><Plus size={12} /></button>
                        </div>
                        <span className="font-display font-bold text-[#7df9ff]">{formatRaw(i.price[lang === "tr" ? "try" : "usd"] * i.qty)}</span>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {items.length > 0 && (
              <div className="px-6 py-6 border-t border-white/[0.06] bg-[#0a0a0a]/60" data-testid="cart-summary">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-[#8a8a8a]"><span>{t.cart.subtotal}</span><span data-testid="cart-subtotal">{formatRaw(sub)}</span></div>
                  <div className="flex justify-between text-[#8a8a8a]"><span>{t.cart.vat}</span><span data-testid="cart-vat">{formatRaw(vat)}</span></div>
                  <div className="flex justify-between pt-3 border-t border-white/[0.06] font-display font-bold text-lg text-[#f4f2ec]">
                    <span>{t.cart.total}</span><span className="text-gold-gradient" data-testid="cart-total">{formatRaw(sub + vat)}</span>
                  </div>
                </div>
                <button className="btn-gold w-full justify-center mt-5" onClick={checkout} data-testid="cart-checkout-button">
                  <Lock size={14} /> {t.cart.checkout}
                </button>
                <button className="w-full mt-3 text-xs text-[#666] hover:text-[#a3a3a3] transition-colors font-mono tracking-[0.15em] uppercase" onClick={clear} data-testid="cart-clear-button">
                  {t.cart.clear}
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
